import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { KoremarginLogo } from '../ui/KoremarginLogo'

interface FormData {
  email: string
  password: string
  confirmPassword: string
}

export function SignupPage() {
  const { signUp, signInWithOAuth } = useAuth()
  const [serverError, setServerError] = useState<string | null>(null)
  const [oauthError, setOauthError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  const password = watch('password')

  async function onSubmit(data: FormData) {
    setServerError(null)
    const err = await signUp(data.email, data.password)
    if (err) {
      setServerError(err)
    } else {
      setSuccess(true)
    }
  }

  async function handleOAuth(provider: 'google' | 'linkedin_oidc') {
    setOauthError(null)
    const err = await signInWithOAuth(provider)
    if (err) setOauthError(err)
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[--km-bg] flex items-center justify-center p-4">
        <div className="w-full max-w-sm text-center">
          <div className="flex flex-col items-center mb-8 gap-3">
            <KoremarginLogo variant="full" />
          </div>
          <div className="bg-[--km-surface] border border-[--km-border] rounded-xl p-6 shadow-sm">
            <div className="text-3xl mb-3">✉️</div>
            <h2 className="text-lg font-semibold text-[--km-text-primary] mb-2">Controlla la tua email</h2>
            <p className="text-sm text-[--km-text-secondary]">
              Abbiamo inviato un link di conferma. Clicca il link nell'email per attivare il tuo account.
            </p>
          </div>
          <p className="mt-4 text-sm text-[--km-text-secondary]">
            Hai già confermato?{' '}
            <Link to="/login" className="text-[--km-primary] hover:underline font-medium">
              Accedi
            </Link>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[--km-bg] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8 gap-3">
          <h1 className="sr-only">Koremargin</h1>
          <KoremarginLogo variant="full" />
          <p className="text-sm text-[--km-text-secondary]">
            Business Intelligence Suite
          </p>
        </div>

        <div className="bg-[--km-surface] border border-[--km-border] rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-[--km-text-primary] mb-5">Crea un account</h2>

          {/* OAuth providers */}
          <div className="space-y-2 mb-4">
            <button
              type="button"
              onClick={() => handleOAuth('google')}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] hover:bg-[--km-surface-hover,rgba(255,255,255,0.04)] transition-colors"
            >
              <GoogleIcon />
              Continua con Google
            </button>
            <button
              type="button"
              onClick={() => handleOAuth('linkedin_oidc')}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] hover:bg-[--km-surface-hover,rgba(255,255,255,0.04)] transition-colors"
            >
              <LinkedInIcon />
              Continua con LinkedIn
            </button>
          </div>

          {oauthError && (
            <div role="alert" className="rounded-lg bg-red-50 border border-[--km-error] px-3 py-2 mb-4">
              <p className="text-sm text-[--km-error]">{oauthError}</p>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-[--km-border]" />
            <span className="text-xs text-[--km-text-secondary]">oppure</span>
            <div className="flex-1 h-px bg-[--km-border]" />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-[--km-text-secondary] mb-1"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="tu@azienda.it"
                className="w-full px-3 py-2 text-sm border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] placeholder-[--km-text-secondary] focus:outline-none focus:ring-2 focus:ring-[--km-primary] focus:border-transparent"
                {...register('email', {
                  required: 'Email obbligatoria.',
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: 'Inserire un indirizzo email valido.',
                  },
                })}
              />
              {errors.email && (
                <p role="alert" className="mt-1 text-xs text-[--km-error]">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-[--km-text-secondary] mb-1"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full px-3 py-2 text-sm border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] placeholder-[--km-text-secondary] focus:outline-none focus:ring-2 focus:ring-[--km-primary] focus:border-transparent"
                {...register('password', {
                  required: 'Password obbligatoria.',
                  minLength: { value: 6, message: 'La password deve contenere almeno 6 caratteri.' },
                })}
              />
              {errors.password && (
                <p role="alert" className="mt-1 text-xs text-[--km-error]">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-[--km-text-secondary] mb-1"
              >
                Conferma password
              </label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="••••••••"
                className="w-full px-3 py-2 text-sm border border-[--km-border] rounded-lg bg-[--km-bg] text-[--km-text-primary] placeholder-[--km-text-secondary] focus:outline-none focus:ring-2 focus:ring-[--km-primary] focus:border-transparent"
                {...register('confirmPassword', {
                  required: 'Conferma la password.',
                  validate: (v) => v === password || 'Le password non coincidono.',
                })}
              />
              {errors.confirmPassword && (
                <p role="alert" className="mt-1 text-xs text-[--km-error]">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {serverError && (
              <div
                role="alert"
                className="rounded-lg bg-red-50 border border-[--km-error] px-3 py-2"
              >
                <p className="text-sm text-[--km-error]">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-2 px-4 text-sm font-medium text-white bg-[--km-primary] rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isSubmitting ? 'Registrazione in corso…' : 'Crea account'}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-sm text-[--km-text-secondary]">
          Hai già un account?{' '}
          <Link to="/login" className="text-[--km-primary] hover:underline font-medium">
            Accedi
          </Link>
        </p>
      </div>
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}
